import { Session } from 'next-auth'
import {
  Customer,
  Result,
  ChangePasswordOptions,
  CustomerAddress,
  UpdateCustomerRequest,
  SavePaymentMethodRequest,
  DeactivateAccount,
  CustomerRewards,
  WorkatoPrintInvoiceRequest,
  CreateGiftCardRequest,
  CheckEmailRequest,
  EmailStatus,
  EmailSubscriptionsResponse,
  UpdateEmailSubscriptionsRequest,
} from 'shared-types'
import { ApiError } from '~/helpers/apiError'
import { IAccountClient } from '~/types/Client'
import { request } from './request'

export class AccountClient implements IAccountClient {
  public updateAccount = async (
    payload: CustomerAddress,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request<Customer>(
      {
        url: '/bff/account/customer',
        method: 'PUT',
        body: payload,
      },
      session
    )
  }

  public changePassword = async (
    payload: ChangePasswordOptions,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/change/password',
        method: 'PUT',
        body: payload,
      },
      session
    )
  }

  public addAddress = async (
    payload: CustomerAddress,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/address',
        method: 'POST',
        body: payload,
      },
      session
    )
  }

  public updateAddress = async (
    payload: CustomerAddress,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/address',
        method: 'PUT',
        body: payload,
      },
      session
    )
  }

  public deleteAddress = async (
    id: string,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: `/bff/account/address/${id}`,
        method: 'DELETE',
      },
      session
    )
  }

  public updateCustomerPreferences = async (
    payload: UpdateCustomerRequest,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/customer/preferences',
        method: 'POST',
        body: payload,
      },
      session
    )
  }

  public getCustomer = async (
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/details',
        method: 'GET',
      },
      session
    )
  }

  public addPaymentMethods = async (
    payload: SavePaymentMethodRequest,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/payment-method',
        method: 'POST',
        body: payload,
      },
      session
    )
  }

  public updatePaymentMethods = async (
    refId: string,
    payload: SavePaymentMethodRequest,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: `/bff/account/payment-method/${refId}`,
        method: 'PUT',
        body: payload,
      },
      session
    )
  }

  public deletePaymentMethods = async (
    id: string,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: `/bff/account/payment-method/${id}`,
        method: 'DELETE',
      },
      session
    )
  }

  public deleteTyroPaymentMethod = async (
    id: string,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: `/bff/account/tyro-payment-method/${id}`,
        method: 'DELETE',
      },
      session
    )
  }

  public setDefaultTyroCreditCard = async (
    cardId: string,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: `/bff/account/tyro-payment-method/set-default/${cardId}`,
        method: 'PUT',
      },
      session
    )
  }

  public addGiftCard = async (
    payload: CreateGiftCardRequest,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/gift-card',
        method: 'POST',
        body: payload,
      },
      session
    )
  }

  public deleteGiftCards = async (
    id: string,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: `/bff/account/gift-card/${id}`,
        method: 'DELETE',
      },
      session
    )
  }

  public deactivateAccount = async (
    payload: DeactivateAccount,
    session: Session
  ): Promise<Result<Customer, ApiError>> => {
    return request(
      {
        url: '/bff/account/customer/deactivate',
        method: 'PATCH',
        body: payload,
      },
      session
    )
  }

  public getLoyaltyPoints = async (
    session: Session
  ): Promise<Result<CustomerRewards, ApiError>> => {
    return request(
      {
        url: '/bff/account/loyalty-points',
        method: 'GET',
      },
      session
    )
  }

  public printInvoice = async (
    payload: WorkatoPrintInvoiceRequest,
    session: Session
  ): Promise<Result<Blob, ApiError>> => {
    return request(
      {
        url: '/bff/account/print-invoice',
        method: 'POST',
        body: payload,
        responseType: 'arraybuffer',
      },
      session
    )
  }

  async deepEmailChecker(
    session: Session,
    payload: CheckEmailRequest
  ): Promise<Result<EmailStatus, ApiError>> {
    return request<EmailStatus>(
      {
        url: '/bff/users/deep-email-ckecker',
        method: 'POST',
        body: payload,
      },
      session
    )
  }

  public getEmailSubscriptions = (
    session: Session
  ): Promise<Result<EmailSubscriptionsResponse, ApiError>> => {
    return request(
      {
        url: '/bff/account/email-subscriptions',
        method: 'GET',
      },
      session
    )
  }

  public updateEmailSubscriptions = async (
    payload: UpdateEmailSubscriptionsRequest,
    session: Session
  ): Promise<Result<void, ApiError>> => {
    return request(
      {
        url: '/bff/account/email-subscriptions',
        method: 'POST',
        body: payload,
      },
      session
    )
  }
}
